"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, SlidersHorizontal, ShieldAlert, Wind, Wrench, Boxes, Truck, ArrowUpRight, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface EvaluationFactor {
  id: string;
  datum: string;
  title: string;
  category: string;
  iconName: "sliders" | "shield" | "wind" | "wrench" | "boxes" | "truck" | "check";
  primarySpec: string;
  comparisonItems: string[];
}
const factors: EvaluationFactor[] = [{
  id: "factor-1",
  datum: "PAR-01",
  title: "Equipment Condition",
  category: "CORE MACHINE",
  iconName: "check",
  primarySpec: "Compare condition, refurbishment status, and cosmetic wear",
  comparisonItems: ["New factory units versus used or refurbished mechanical bases", "Operating history and service records of core mechanical drives", "Cosmetic condition of the bull body, saddle, and exterior finish"]
}, {
  id: "factor-2",
  datum: "PAR-02",
  title: "Control Console",
  category: "ELECTRONICS",
  iconName: "sliders",
  primarySpec: "Compare manual controls, automated programs, and safety stop features",
  comparisonItems: ["Manual joystick operation versus pre-set automated ride profiles", "Presence, accessibility, and condition of operator emergency stops", "Whether digital ride timers or remote controls are included"]
}, {
  id: "factor-3",
  datum: "PAR-03",
  title: "Safety Inflatable Arena",
  category: "SURROUND",
  iconName: "shield",
  primarySpec: "Compare arena footprint, wall configuration, and material durability",
  comparisonItems: ["Overall inflated arena footprint and required operating clearance", "Wall height, perimeter cushioning, and entry step design", "Seam reinforcement, tie-down anchor locations, and vinyl durability"]
}, {
  id: "factor-4",
  datum: "PAR-04",
  title: "Commercial Air Blower",
  category: "PNEUMATICS",
  iconName: "wind",
  primarySpec: "Confirm blower compatibility, power draw, and inflation fittings",
  comparisonItems: ["Whether a dedicated commercial inflation blower is included in the package", "Electrical supply requirements needed for continuous inflation", "Condition of blower inflation tubes, fastening straps, and deflation vents"]
}, {
  id: "factor-5",
  datum: "PAR-05",
  title: "Included Ancillary Equipment",
  category: "HARDWARE",
  iconName: "boxes",
  primarySpec: "Review handling dollys, anchoring kits, and protective accessories",
  comparisonItems: ["Integrated wheels, dolly systems, or transport handles for machine transit", "Tie-down straps, floor anchor hardware, and leveling shims", "Protective storage covers and dedicated console stands"]
}, {
  id: "factor-6",
  datum: "PAR-06",
  title: "Replacement Parts Availability",
  category: "LOGISTICS",
  iconName: "wrench",
  primarySpec: "Verify ongoing access to wear parts, motors, and technical support",
  comparisonItems: ["Availability and standard sourcing for routine replacement wear parts", "Lead times and supplier support for replacement motors and drive components", "Direct technical assistance and maintenance documentation from the seller"]
}, {
  id: "factor-7",
  datum: "PAR-07",
  title: "Freight & Shipping Logistics",
  category: "FULFILLMENT",
  iconName: "truck",
  primarySpec: "Confirm delivery location handling, liftgate service, and freight fees",
  comparisonItems: ["Whether freight covers direct site delivery, terminal pickup, or dock delivery", "Liftgate service availability and requirements for offloading heavy crates", "All applicable freight charges, handling fees, and estimated transit times"]
}];
function FactorIcon({
  name
}: {
  name: EvaluationFactor["iconName"];
}) {
  switch (name) {
    case "sliders":
      return <SlidersHorizontal className="size-4 text-primary" aria-hidden="true" />;
    case "shield":
      return <ShieldAlert className="size-4 text-primary" aria-hidden="true" />;
    case "wind":
      return <Wind className="size-4 text-primary" aria-hidden="true" />;
    case "wrench":
      return <Wrench className="size-4 text-primary" aria-hidden="true" />;
    case "boxes":
      return <Boxes className="size-4 text-primary" aria-hidden="true" />;
    case "truck":
      return <Truck className="size-4 text-primary" aria-hidden="true" />;
    case "check":
    default:
      return <CheckCircle2 className="size-4 text-primary" aria-hidden="true" />;
  }
}
export default function Valuation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="bull-riding-machine-price" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div id="bullridingmachineprice" className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="border-b border-border pb-8 mb-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="inline-flex items-center gap-2 border border-border bg-muted/60 px-3 py-1 text-xs font-mono tracking-wider uppercase text-muted-foreground">
              <span className="inline-block size-1.5 bg-primary" aria-hidden="true" />
              <span>EQUIPMENT VALUATION & NOMENCLATURE</span>
            </div>
            <span className="text-xs font-mono text-muted-foreground tracking-widest uppercase">
              SPECIFICATION COMPARISON GUIDE
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold uppercase tracking-tight text-foreground max-w-4xl">
            Bull Riding Machine Price vs. Mechanical Bull Price
          </h2>

          <div className="mt-6 border-l-2 border-primary pl-4 sm:pl-6 max-w-4xl bg-card p-4 border border-border border-l-primary">
            <p className="text-base sm:text-lg leading-relaxed text-foreground font-sans">
              Buyers often use &apos;bull riding machine&apos; and &apos;mechanical bull&apos; to describe the same general type of equipment. Therefore, a bull riding machine price should be compared using the same factors: condition, controller, safety arena, blower, included equipment, parts availability and shipping. Do not assume that a listing described as a bull riding machine includes a different package simply because the wording changes. Review the actual product details.
            </p>
          </div>
        </motion.div>

        {/* Evaluation Factor Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {factors.map((factor, index) => <motion.div key={factor.id} data-index={index} variants={itemVariants} className="bg-card p-6 flex flex-col justify-between transition-[border-color] duration-180 hover:border-primary border border-transparent">
              <div>
                {/* Card Top Datum Strip */}
                <div className="flex items-center justify-between gap-2 pb-4 mb-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <FactorIcon name={factor.iconName} />
                    <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground">
                      {factor.datum}
                    </span>
                  </div>
                  <Badge variant="outline" className="rounded-none border-border font-mono text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/40">
                    {factor.category}
                  </Badge>
                </div>

                {/* Factor Heading & Specification Scope */}
                <h3 className="text-xl font-bold uppercase tracking-tight text-foreground font-serif">
                  {factor.title}
                </h3>
                <p className="mt-1 text-xs font-mono font-medium text-primary">
                  {factor.primarySpec}
                </p>

                {/* Verification Checklist */}
                <div className="mt-5 space-y-2.5">
                  <span className="block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Included Package Verification:
                  </span>
                  <ul className="space-y-2 text-xs sm:text-sm text-muted-foreground">
                    {factor.comparisonItems.map((item, itemIdx) => <li key={itemIdx} className="flex items-start gap-2">
                        <span className="mt-1 block size-1 bg-foreground/60 shrink-0" aria-hidden="true" />
                        <span className="leading-snug text-foreground/90">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Bottom Datum Anchor */}
              <div className="mt-6 pt-4 border-t border-border flex items-center justify-between font-mono text-[11px] text-muted-foreground">
                <span>INDEX 0{index + 1} / 07</span>
                <span className="uppercase text-[10px] text-primary font-bold">MANDATORY CHECK</span>
              </div>
            </motion.div>)}

          {/* Quick Summary / Action Plate filling the 8th slot in the grid */}
          <motion.div data-index={7} variants={itemVariants} className="bg-muted/40 p-6 flex flex-col justify-between border border-transparent md:col-span-2 lg:col-span-2">
            <div>
              <div className="flex items-center gap-2 pb-4 mb-4 border-b border-border">
                <HelpCircle className="size-4 text-primary" aria-hidden="true" />
                <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                  VALUATION AUDIT CHECKLIST
                </span>
              </div>
              <h3 className="text-2xl font-serif font-bold uppercase text-foreground">
                Need a Line-by-Line Spec Quote?
              </h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Before purchasing any machine advertised as a &apos;bull riding machine&apos; or &apos;mechanical bull&apos;, review the complete price guide or speak directly with our equipment team to confirm exact equipment details, included accessories, and shipping arrangements before purchasing.
              </p>
            </div>

            <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center gap-3">
              <Button asChild className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/MechanicalBullPriceGuidePage">
                  <span>View Complete Price Guide</span>
                  <ArrowUpRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-none border-border">
                <Link to="/ContactPage">Request Equipment Spec Sheet</Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}